import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/notifications").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    const iv = setInterval(load, 30_000);
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => { clearInterval(iv); document.removeEventListener("mousedown", onDoc); };
  }, []);

  const unread = items.filter((i) => !i.read).length;
  const markAll = async () => { await api.post("/notifications/mark-all-read"); load(); };
  const markOne = async (n) => { if (!n.read) { await api.post(`/notifications/${n.id}/read`); load(); } };

  return (
    <div ref={ref} className="relative">
      <button data-testid="notif-bell-button" onClick={() => setOpen(!open)} className="relative p-2 border border-zinc-300 bg-white hover:bg-zinc-50">
        <Bell className="w-4 h-4" />
        {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#002FA7] text-white text-[10px] font-mono font-bold flex items-center justify-center">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 max-h-[80vh] bg-white border border-zinc-300 shadow-lg z-50 flex flex-col" data-testid="notif-panel">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div><div className="label-eyebrow">Notifications</div><div className="text-sm font-semibold">{unread} unread</div></div>
            {unread > 0 && <button data-testid="notif-mark-all-read" onClick={markAll} className="text-[11px] uppercase tracking-wider text-[#002FA7] hover:underline inline-flex items-center gap-1"><CheckCheck className="w-3 h-3" />Mark all read</button>}
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No notifications yet.</div>}
            {items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => markOne(n)} className={`w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 ${n.read ? "" : "bg-blue-50/50"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{n.type.replace(/_/g, " ")}</div>
                    <div className="text-sm text-zinc-950">{n.message}</div>
                    <div className="text-[11px] font-mono text-zinc-500 mt-1">{fmtDate(n.at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 bg-[#002FA7] mt-1 shrink-0" />}
                </div>
                {n.type === "order_received" && n.entity_id && <Link to="/orders" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-wider text-[#002FA7] hover:underline">View orders →</Link>}
                {n.type === "order_approved" && n.entity_id && <Link to="/orders" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-wider text-[#002FA7] hover:underline">View my order →</Link>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
